import { useState, useEffect, useCallback } from 'react';
import { NavItem } from '@/shared/types/navigation';

const MENU_ORDER_KEY = 'menu_order';

type StoredNavItem = {
  name: string;
  path?: string;
  subItems?: {
    name: string;
    path: string;
    pro?: boolean;
    new?: boolean;
  }[];
};

/**
 * Восстанавливает NavItems из сохраненных данных, добавляя иконки из defaultItems
 * Вынесено в отдельную функцию для переиспользования
 */
const reconstructNavItems = (
  savedItems: StoredNavItem[],
  defaultItems: NavItem[]
): NavItem[] | null => {
  try {
    const allItems = savedItems
      .map(savedItem => {
        const defaultItem = defaultItems.find(item => item.name === savedItem.name);
        if (!defaultItem) return null;
        return {
          ...savedItem,
          icon: defaultItem.icon,
        };
      })
      .filter((item): item is NavItem => item !== null);

    // Возвращаем только если все элементы найдены
    if (allItems.length === defaultItems.length) {
      return allItems;
    }
  } catch (e) {
    console.error('Error reconstructing menu items:', e);
  }
  return null;
};

/**
 * Загружает сохраненный порядок меню из localStorage
 */
const loadSavedMenuOrder = (defaultItems: NavItem[]): NavItem[] => {
  const savedOrder = localStorage.getItem(MENU_ORDER_KEY);
  if (!savedOrder) {
    return defaultItems;
  }

  try {
    const savedItems = JSON.parse(savedOrder) as StoredNavItem[];
    const reconstructed = reconstructNavItems(savedItems, defaultItems);
    return reconstructed || defaultItems;
  } catch (e) {
    console.error('Error parsing saved menu order:', e);
    return defaultItems;
  }
};

export const useMenuOrder = (defaultItems: NavItem[]) => {
  const [items, setItems] = useState<NavItem[]>(() => loadSavedMenuOrder(defaultItems));

  // Обработчик изменений в localStorage (для синхронизации между вкладками)
  const handleStorageChange = useCallback(
    (e: StorageEvent) => {
      if (e.key !== MENU_ORDER_KEY || !e.newValue) {
        return;
      }

      try {
        const savedItems = JSON.parse(e.newValue) as StoredNavItem[];
        const reconstructed = reconstructNavItems(savedItems, defaultItems);
        if (reconstructed) {
          setItems(reconstructed);
        }
      } catch (e) {
        console.error('Error handling storage change:', e);
      }
    },
    [defaultItems]
  );

  // Подписываемся на изменения в localStorage
  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [handleStorageChange]);

  const saveOrder = useCallback(
    (newItems: NavItem[]) => {
      // Сохраняем только необходимые данные без React элементов
      const itemsToSave = newItems.map(item => ({
        name: item.name,
        path: item.path,
        subItems: item.subItems,
      }));

      setItems(newItems);
      localStorage.setItem(MENU_ORDER_KEY, JSON.stringify(itemsToSave));

      // Отправляем событие для синхронизации с другими компонентами
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: MENU_ORDER_KEY,
          newValue: JSON.stringify(itemsToSave),
        })
      );
    },
    []
  );

  return {
    items,
    saveOrder,
  };
};
